import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';

import { User } from '../models/user.model';
import { AuthError } from '../models/auth-error.model';
import { API, APP } from '../../constants/api-endpoints';
import { MsalService } from '@azure/msal-angular';
import { loginRequest } from '../msal.config';
import { environment } from '../../../../environments/environment';
import { SessaoHeartbeatService } from '../../services/operacao-sessao/sessao-heartbeat.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly heartbeat = inject(SessaoHeartbeatService);

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();

  private readonly authErrorSubject = new BehaviorSubject<string | null>(null);
  readonly authError$ = this.authErrorSubject.asObservable();

  private loginInProgress = false;

  async login(): Promise<void> {
    if (this.loginInProgress) {
      return;
    }

    this.loginInProgress = true;
    this.clearAuthError();

    try {
      await this.msalService.instance.initialize();
      await this.msalService.instance.handleRedirectPromise();

      await this.msalService.loginRedirect(loginRequest);
    } catch (error) {
      console.error('LOGIN ERROR:', error);
      this.authErrorSubject.next('Não foi possível iniciar o login.');
      this.loginInProgress = false;

      throw error;
    }
  }

  async loginWithAnotherAccount(): Promise<void> {
    if (this.loginInProgress) {
      return;
    }

    this.loginInProgress = true;
    this.clearAuthError();

    try {
      await this.msalService.instance.initialize();
      await this.msalService.instance.handleRedirectPromise();

      this.msalService.instance.setActiveAccount(null);

      await this.msalService.loginRedirect({
        ...loginRequest,
        prompt: 'select_account'
      });
    } catch (error) {
      console.error('LOGIN WITH ANOTHER ACCOUNT ERROR:', error);
      this.authErrorSubject.next('Não foi possível iniciar o login com outra conta.');
      this.loginInProgress = false;

      throw error;
    }
  }

  async logout(): Promise<void> {
    this.currentUserSubject.next(null);
    this.clearAuthError();
    this.loginInProgress = false;

    // Encerra todas as sessoes de operacao ativas do usuario ANTES de
    // redirecionar para o logout do Azure AD. Assim os empreendimentos
    // que ele estava usando ficam liberados imediatamente para outros
    // operadores, sem precisar esperar os 5min do lazy cleanup do
    // backend. A promise nao bloqueia o logout mesmo se a chamada
    // falhar — o heartbeat.encerrarTodasDoUsuario ja lida com isso.
    await this.heartbeat.encerrarTodasDoUsuario();

    await this.msalService.instance.initialize();

    this.msalService.instance.setActiveAccount(null);

    await this.msalService.logoutRedirect({
      postLogoutRedirectUri: APP.login
    });
  }

  getMe(): Observable<User | null> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return of(null);
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: [environment.azureAd.apiScope]
      })
    ).pipe(
      switchMap((tokenResult) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${tokenResult.accessToken}`
        });

        return this.http.get<User>(API.auth.me, { headers });
      }),
      tap((user) => {
        this.currentUserSubject.next(user);
        this.clearAuthError();
      }),
      catchError((error: HttpErrorResponse) => {
        this.currentUserSubject.next(null);

        if (error.status === 401) {
          const apiError = error.error as AuthError;

          if (apiError?.code === 'AUTH_UNAUTHORIZED') {
            this.authErrorSubject.next(apiError.message || 'Acesso não autorizado');
            return of(null);
          }

          this.authErrorSubject.next('Acesso não autorizado');
          return of(null);
        }

        this.authErrorSubject.next('Não foi possível validar o acesso.');
        console.error('AUTH ERROR:', error);
        return of(null);
      })
    );
  }

  registrarLogAcesso(): Observable<void> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return of(void 0);
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: [environment.azureAd.apiScope]
      })
    ).pipe(
      switchMap((tokenResult) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${tokenResult.accessToken}`
        });

        return this.http.post<void>(API.auth.logAcesso, {}, { headers });
      }),
      catchError((error) => {
        console.error('Erro ao registrar log de acesso:', error);
        return of(void 0);
      })
    );
  }

  checkAuth(): Observable<boolean> {
    return this.getMe().pipe(
      map((user) => !!user?.authenticated)
    );
  }

  hasPermission(nomeTela: string): boolean {
    const user = this.currentUserSubject.value;

    if (!user || !user.permissoes || user.permissoes.length === 0) {
      return false;
    }

    return user.permissoes.some(
      (p) =>
        p.nomeTela.toLowerCase() === nomeTela.toLowerCase() &&
        p.visualizar
    );
  }

  clearAuthError(): void {
    this.authErrorSubject.next(null);
  }
}